import type { Project } from '../domain/schemas';
import { Observable } from '../lib/store';
import { ExportError, renderSong, renderToWav, wavFileName, type OfflineContextFactory, type RenderAudio } from './render';

export interface ExportState {
  status: 'idle' | 'rendering' | 'done' | 'error';
  startedAt?: number;
  /** Seconds of audio being rendered (for progress messaging). */
  renderingSeconds?: number;
  fileName?: string;
  url?: string;
  summary?: string;
  error?: string;
}

const formatTime = (s: number) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`;
const dbfs = (peak: number) => (peak > 0 ? `${(20 * Math.log10(peak)).toFixed(1)} dBFS` : 'silent');

/** Renders WAV files offline and hands them to the browser as downloads. One at a time. */
export class WavExporter {
  readonly state = new Observable<ExportState>({ status: 'idle' });
  private readonly createContext: OfflineContextFactory | undefined;
  private readonly download: (url: string, fileName: string) => void;

  private readonly audio: RenderAudio | undefined;

  constructor(
    createContext?: OfflineContextFactory,
    download: (url: string, fileName: string) => void = triggerDownload,
    audio?: RenderAudio,
  ) {
    this.createContext = createContext;
    this.download = download;
    this.audio = audio;
  }

  /** Renders and downloads; resolves with a one-line summary. */
  async export(project: Project): Promise<string> {
    if (this.state.get().status === 'rendering') throw new ExportError('render-failed', 'An export is already running.');
    this.revoke();
    this.state.set({ status: 'rendering', startedAt: Date.now() });
    try {
      const result = await renderSong(project, this.createContext, undefined, { audio: this.audio });
      this.state.update({ renderingSeconds: result.songSeconds });
      const wav = renderToWav(result);
      const url = URL.createObjectURL(new Blob([wav], { type: 'audio/wav' }));
      const fileName = wavFileName(project);
      const mb = (wav.byteLength / 1024 / 1024).toFixed(1);
      const summary =
        `Exported ${fileName} · ${formatTime(result.durationSec)} (song ${formatTime(result.songSeconds)} + release tail) · ` +
        `44.1 kHz 16-bit stereo · ${mb} MB · peak ${dbfs(result.peak)}` +
        (result.normalizedDb < 0 ? ` · lowered ${Math.abs(result.normalizedDb)} dB to prevent clipping` : '') +
        ` · rendered in ${(result.renderMs / 1000).toFixed(1)} s` +
        (result.droppedNotes > 0 ? ` · warning: ${result.droppedNotes} note(s) could not be scheduled` : '') +
        (result.cleanupErrors > 0 ? ` · note: ${result.cleanupErrors} cleanup step(s) failed (audio unaffected)` : '');
      this.state.set({ status: 'done', url, fileName, summary });
      this.download(url, fileName);
      return summary;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Export failed.';
      this.state.set({ status: 'error', error: message });
      throw error;
    }
  }

  downloadAgain(): void {
    const { url, fileName } = this.state.get();
    if (url && fileName) this.download(url, fileName);
  }

  dispose(): void {
    this.revoke();
  }

  private revoke(): void {
    const { url } = this.state.get();
    if (url) URL.revokeObjectURL(url);
  }
}

export function triggerDownload(url: string, fileName: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}
